/**
 * Pretty-print the per-reviewer-flag detail blob into a single human
 * line for the V2 Captions Editor sidebar. Falls back to a generic
 * "key: value · key: value" summary if no specific renderer matches
 * — keeps the editor useful even after we add a new flag_type
 * without updating this map.
 *
 * Lifted out of CaptionsEditorPageV2.jsx so the behavior is testable
 * without pulling the whole page module (which transitively touches
 * redux selectors, the action thunks, and wavesurfer).
 *
 *   flagDetailText({ flag_type: 'name_substituted',
 *                    detail: { original: 'Nick', replaced: 'Nik' } })
 *   // → 'Replaced "Nick" → "Nik"'
 */
export function flagDetailText(flag) {
  const d = flag?.detail ?? {};
  switch (flag?.flag_type) {
    case 'name_substituted':
      return d.original && d.replaced
        ? `Replaced "${d.original}" → "${d.replaced}"`
        : null;
    case 'name_unmatched':
      return d.first_found && d.expected_last
        ? `Found "${d.first_found}" but no "${d.expected_last}" follow-up.`
        : null;
    case 'low_confidence_segment':
      return d.avg_logprob != null
        ? `avg_logprob = ${Number(d.avg_logprob).toFixed(2)}`
        : null;
    case 'high_no_speech_prob':
      return d.no_speech_prob != null
        ? `no_speech_prob = ${Number(d.no_speech_prob).toFixed(2)}`
        : null;
    case 'large_pause_in_audio':
      return d.gap_sec != null
        ? `${Number(d.gap_sec).toFixed(1)}s gap between segments`
        : null;
    case 'duration_mismatch':
      return d.delta_ratio != null && d.expected_sec != null && d.transcribed_sec != null
        ? `Expected ${Math.round(d.expected_sec)}s, transcribed ${Math.round(d.transcribed_sec)}s (${(d.delta_ratio * 100).toFixed(0)}% off)`
        : null;
    case 'unexpected_language':
      return d.detected && d.expected
        ? `Detected "${d.detected}", expected "${d.expected}".`
        : null;
    case 'brand_term_lowercase':
    case 'vocabulary_miss':
    default:
      return Object.keys(d).length > 0
        ? Object.entries(d)
            .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
            .join(' · ')
        : null;
  }
}
